import React from 'react'
import { Truck, Undo2, AlertTriangleIcon, Package } from 'lucide-react'
import InfoButton from './InfoButton'
import { CountryDetails } from '../services/api'
import { IEstimatedDispatchDays } from '@/context/ProductDataStore'
import { formatPriceWithSymbol } from '@/utils/priceFormatting'
import { productDeliveryInfoHtml } from '@/utils/deliveryPolicyText'
import { useDeliveryPolicy } from '@/hooks/useDeliveryPolicy'
import useRegionStore from '@/context/RegionStore'

interface ProductShippingInfoProps {
    currencySymbol: string
    freeDeliveryThreshold: number
    countryDetails: CountryDetails | null
    formattedUpdateDate: string
    estimatedDispatchDays?: IEstimatedDispatchDays | null
    primaryTextColor?: string
    iconColor?: string
}

const ProductShippingInfo: React.FC<ProductShippingInfoProps> = ({
    currencySymbol,
    freeDeliveryThreshold,
    countryDetails,
    formattedUpdateDate,
    estimatedDispatchDays,
    primaryTextColor,
    iconColor
}) => {
    const formatDispatchEstimate = (estimate?: IEstimatedDispatchDays | null): string | null => {
        if (!estimate) {
            return null
        }

        const { min, max, unit } = estimate
        const hasMin = typeof min === 'number'
        const hasMax = typeof max === 'number'
        if (!hasMin && !hasMax) {
            return null
        }

        const unitLabel = unit ? unit.replace(/_/g, ' ') : 'days'
        if (hasMin && hasMax && min !== max) {
            return `${min}-${max} ${unitLabel}`
        }
        const value = hasMin ? min : max
        return `${value} ${unitLabel}`
    }

    const dispatchEstimateLabel = formatDispatchEstimate(estimatedDispatchDays)
    // The visitor's own delivery options, from the live settings; the button waits for them.
    const { policy } = useDeliveryPolicy()
    const { chosenRegion } = useRegionStore()
    const deliveryInfo = policy && chosenRegion ? productDeliveryInfoHtml(policy, chosenRegion) : null

    return (
        <div className='flex flex-col gap-6 md:gap-4 mt-4 md:mt-2'>
            {/* Christmas Delivery Notice */}
            {/* <div className="bg-green/40 border-l-4 border-green p-4 rounded-lg mb-2">
                <div className="flex items-start gap-3">
                    <span className="text-2xl">🎄</span>
                    <div className="flex-1">
                        <div className="font-bold text-green text-base mb-1">Christmas Delivery - Order by Dec 20</div>
                        <div className="text-sm text-gray-100">
                            Order by Saturday 20 December (23:59 GMT) for delivery by Wednesday 24 December. UK mainland only.
                        </div>
                    </div>
                </div>
            </div> */}
            <div className="flex flex-col gap-2 bg-grey p-5 rounded-2xl">
                {dispatchEstimateLabel && (
                    <div className="flex flex-row gap-2 items-center">
                        <Package className={"text-black"} />
                        <h4 className="font-body text-base text-black">Dispatched: {dispatchEstimateLabel}</h4>
                    </div>
                )}
                <div className="flex flex-row gap-2 items-center">
                    <Truck className={"text-black"} />
                    <h4 className={`font-body text-base text-black`}>Free Delivery Over {formatPriceWithSymbol(currencySymbol, (freeDeliveryThreshold ?? 5000) / 100)}</h4>
                    {deliveryInfo && <InfoButton title="Delivery Information" text={deliveryInfo} />}
                </div>
                {countryDetails?.returns?.active && (
                    <div className="flex flex-row gap-2 items-center">
                        <Undo2 className={"text-black"} />
                        {countryDetails?.returns?.active ? (
                            <h4 className={`font-body text-base text-black`}>{countryDetails.returns.returnDays}-Day Returns</h4>
                        ) : (
                            <h4 className={`font-body text-base text-black`}>${countryDetails?.returns?.returnDays || 20}-Day Returns</h4>
                        )}
                        <InfoButton
                            title="Returns"
                            text={`
                            <div>
                                <p><strong>Last Updated: ${formattedUpdateDate}</strong></p>
                                <p>Returns accepted within ${countryDetails?.returns?.returnDays || 20} days of delivery. Conditions apply.</p>
                                <p>The product must be undamaged, with its original packaging, and with all the original contents.</p>
                                <p>Email us at <a href="mailto:returns@kibu.family" class="text-primary hover:underline">returns@kibu.family</a></p>
                                <p class="mt-4">
                                    <a 
                                    href="/legal/delivery-and-returns/"
                                    class="text-primary hover:underline"
                                >
                                    View our full returns policy for conditions →
                                </a>
                                </p>
                            </div>
                        `}
                        />
                    </div>
                )}
            </div>
            <div className="flex items-center gap-3 border border-1 border-dashed border-[#494949] rounded-2xl p-2 pl-4 pr-4">
                <AlertTriangleIcon className={`w-24 h-24 md:w-12 md:h-12 stroke-2 ${iconColor || 'text-[#494949]'}`} />
                <p className={`font-medium leading-[18px] ${primaryTextColor || 'text-black'}`}>
                    WARNING! Choking Hazard. Small parts, not for children under 3 yrs. Long cable. Strangulation hazard. For use under adult supervision.
                </p>
            </div>
        </div>
    )
}

export default ProductShippingInfo
